import { ModalCloseSVG } from "@/utils/svg";
import React, { ReactNode, useEffect, useRef } from "react";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  children: ReactNode;
  width?: string;
  maxWidth?: string;
  showCloseButton?: boolean;
  footer?: ReactNode;
  contentClassName?: string;
  closeOnOutsideClick?: boolean;
  className?: string;
  /** Constrains modal to viewport and enables internal flex shrink (for tall modals e.g. crop). */
  fitViewport?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  titleClassName = "text-[24px] font-medium text-gray-900",
  subtitleClassName = "mt-1 text-[14px] font-medium text-[#7B7B7B]",
  children,
  width = "500px",
  maxWidth,
  showCloseButton = true,
  contentClassName,
  closeOnOutsideClick = false,
  className = "",
  fitViewport = false,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Close modal when clicking outside (only if closeOnOutsideClick is true)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        closeOnOutsideClick &&
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    // Handle escape key press (only if closeOnOutsideClick is true)
    const handleEscKey = (event: KeyboardEvent) => {
      if (closeOnOutsideClick && event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscKey);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isOpen, onClose, closeOnOutsideClick]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const panel = (
    <div
      ref={modalRef}
      className={`bg-white shadow-lg rounded-[10px] w-full ${
        fitViewport ? "my-auto flex flex-col" : ""
      } ${className}`.trim()}
      style={{ width, maxWidth }}
    >
      {/* Modal header */}
      <div
        className={`flex items-center justify-between px-6 py-4 border-b rounded-t-[10px] border-gray-200 ${
          fitViewport ? "shrink-0" : ""
        }`}
      >
        <div>
          <h2 className={titleClassName}>{title}</h2>
          {subtitle ? (
            <p className={subtitleClassName}>{subtitle}</p>
          ) : null}
        </div>
        {showCloseButton && (
          <button
            onClick={onClose}
            className="text-dark hover:text-gray-500 cursor-pointer"
          >
            <ModalCloseSVG />
          </button>
        )}
      </div>

      {/* Modal body */}
      <div
        className={`${fitViewport ? "flex flex-col " : ""}${
          contentClassName || "px-6 py-4"
        } rounded-b-[10px]`}
      >
        {children}
      </div>
    </div>
  );

  return (
    <div
      className={`fixed inset-0 z-50 ${
        fitViewport
          ? "overflow-y-auto"
          : "flex items-center justify-center"
      }`}
      style={{ backgroundColor: "rgba(107, 114, 128, 0.5)" }}
    >
      {fitViewport ? (
        <div className="flex min-h-full justify-center p-4">{panel}</div>
      ) : (
        panel
      )}
    </div>
  );
};

export default Modal;
